import React, { useCallback, useMemo } from "react";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature, Point } from "geojson";
import { isEqual } from "lodash";
import mapboxgl, { FeatureIdentifier } from "mapbox-gl";

import { RedoButton, SelectButton, ToolbarConfig, UndoButton } from "fond/map/Toolbar";
import { LayerIds } from "fond/types";
import { isPoint } from "fond/types/geojson";
import { useAppSelector } from "fond/utils/hooks";

import DrawHandler from "../DrawHandler";
import simpleSelectHubs from "../modes/planner/hubs/simpleSelectHubs";

import hubDrawStyles from "./hubDrawStyles";

/**
 * Default drawing tools when drawing points
 */
const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />],
  },
];

const modes = {
  simple_select: simpleSelectHubs,
};

const sources: FeatureIdentifier[] = [{ source: LayerIds.hub }];

/**
 * Component that handles the moving of DROP and DISTRIBUTION hubs in planner projects.
 * The hubs can only be moved along existing fiber cables based on type:
 * - DROP hubs can be moved along DROP and DISTRIBUTION cables;
 * - DISTRIBUTION hubs can only be moved along DISTRIBUTION cables.
 */
const HubHandler: React.FC = () => {
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);

  /**
   * Determines the initial features for drawing. We only want to allow
   * the user to drag hubs of Tier = "DROP" or Tier === "DISTRIBUTION"
   */
  const initialFeatures: Feature<Point>[] = useMemo(
    () =>
      (data?.layers[LayerIds.hub].features.filter(
        (feature) => isPoint(feature) && ["DROP", "DISTRIBUTION"].includes(feature.properties?.Tier) && feature.properties?.Type === "HUB"
      ) || []) as Feature<Point>[],
    [data]
  );

  /**
   * Determines the features that the item being dragged can snap to.
   * - Tier "DISTRUBUTION" can only be snapped to DISTRIBUTION cables;
   * - Tier "DROP" can be snapped to either DROP or DISTRIBUTION cables.
   */
  const snapGetFeatures = useCallback((map: mapboxgl.Map, draw: MapboxDraw) => {
    const selectedFeatures = draw.getSelected();
    const tier = selectedFeatures.features[0].properties?.Tier;
    const layers = map
      .getStyle()
      .layers.filter((layer) => {
        if (layer.type === "line" && layer.source === LayerIds.fibreCable) {
          const { filter } = layer;

          if (tier === "DISTRIBUTION") {
            return isEqual(filter, ["==", ["get", "Tier"], tier]);
          } else if (tier === "DROP") {
            return isEqual(filter, ["==", ["get", "Tier"], "DISTRIBUTION"]) || isEqual(filter, ["==", ["get", "Tier"], "DROP"]);
          }
        }
        return false;
      })
      .map(({ id }) => id);

    return [...map.queryRenderedFeatures(undefined, { layers })];
  }, []);

  /**
   * Hub snapping requires edge-priority (very low snapVertexPriorityDistance) and a larger snap
   * radius so users can reliably snap to cable edges. These override persisted user preferences.
   */
  const options: MapboxDrawOptions = useMemo(
    () =>
      ({
        snap: true,
        snapOptions: {
          snapPx: 20,
          snapVertexPriorityDistance: 0.000000001,
          snapGetFeatures,
        },
      }) as MapboxDrawOptions,
    [snapGetFeatures]
  );

  return (
    <DrawHandler initialFeatures={initialFeatures} sources={sources} styles={hubDrawStyles} config={toolbarConfig} modes={modes} options={options} />
  );
};

export default HubHandler;
